import { useCallback, useEffect, useRef } from "react";

/**
 * ── Live move-path preview ──
 * While we drag a token, stream the route its drop would take (the SAME `movePathCells` set drawn
 * locally) to everyone else, so they see a ghost trail of the impending move. Read `onMovePreview`
 * through a ref so the sender stays stable. No throttle: the drag handler already coalesces to
 * cell-change granularity (one emit per cell crossed), so this fires far slower than the cursor
 * stream. `null` clears the trail (drop / illegal hover).
 */
export function useMovePreviewStream(onMovePreview: ((preview: { cells: string[] } | null) => void) | undefined) {
  const onMovePreviewRef = useRef(onMovePreview);
  onMovePreviewRef.current = onMovePreview;
  const streamMovePreview = useCallback((preview: { cells: string[] } | null) => {
    onMovePreviewRef.current?.(preview);
  }, []);
  // Clear our trail for everyone if this surface unmounts mid-drag (no `dragend` would fire) — no
  // lingering ghost path.
  useEffect(() => () => onMovePreviewRef.current?.(null), []);

  return streamMovePreview;
}
